import React, { useContext, useEffect, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import { DocxEditor } from "@eigenpal/docx-editor-react";
import { AuthContext } from "../contexts/auth";
import Swal from "sweetalert2";

import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";

import {
  getLetteraById,
  downloadExistingTemplate,
  downloadExampleTemplate,
  uploadLetterCustomTemplates,
} from "../services/api_lettere";

export default function LetterDocxLiveEditor() {
  const { token } = useContext(AuthContext);
  const [, params] = useRoute("/lettere/template-editor/:letterId/:sesso");

  const editorRef = useRef(null);

  const letterId = params?.letterId;
  const sesso = String(params?.sesso || "M").toUpperCase();

  const [letter, setLetter] = useState(null);
  const [buffer, setBuffer] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token || !letterId) return;

    let cancelled = false;

    async function loadDocx() {
      try {
        setError("");

        const letterDetail = await getLetteraById(token, Number(letterId), null);
        if (cancelled) return;

        setLetter(letterDetail);

        let response;

        try {
          response = await downloadExistingTemplate(token, letterId, sesso);
        } catch {
          response = await downloadExampleTemplate(token, letterId);
        }

        const arrayBuffer = await response.data.arrayBuffer();

        if (!cancelled) {
          setBuffer(arrayBuffer);
        }
      } catch (err) {
        console.error("Errore caricamento editor DOCX:", err);

        if (!cancelled) {
          setError("Impossibile caricare il template nell’editor.");
        }
      }
    }

    loadDocx();

    return () => {
      cancelled = true;
    };
  }, [token, letterId, sesso]);

  const getEditedDocxBlob = async () => {
    const editor = editorRef.current;

    if (!editor) {
      throw new Error("Editor non pronto.");
    }

    console.log("DocxEditor ref:", editor);

    if (typeof editor.exportDocument === "function") {
      return await editor.exportDocument();
    }

    if (typeof editor.save === "function") {
      return await editor.save();
    }

    if (typeof editor.getDocument === "function") {
      return await editor.getDocument();
    }

    throw new Error("Metodo di esportazione DOCX non trovato sull’editor.");
  };

  const handleSaveTemplate = async () => {
    if (!token || !letterId) return;

    try {
      setSaving(true);
      setError("");

      const docxBlob = await getEditedDocxBlob();

      const file = new File(
        [docxBlob],
        sesso === "F" ? "template_donna.docx" : "template_uomo.docx",
        {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }
      );

      const formData = new FormData();

      if (sesso === "F") {
        formData.append("templateDonna", file);
      } else {
        formData.append("templateUomo", file);
      }

      await uploadLetterCustomTemplates(token, letterId, formData);

      await Swal.fire({
        icon: "success",
        title: "Template salvato",
        text: "Il template personalizzato è stato aggiornato correttamente.",
        confirmButtonColor: "#4f46e5",
      });
    } catch (err) {
      console.error("Errore salvataggio template:", err);

      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Impossibile salvare il template.";

      setError(message);

      await Swal.fire({
        icon: "error",
        title: "Errore",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSaving(false);
    }
  };

  if (error && !buffer) {
    return <div className="p-6 text-sm text-red-600">{error}</div>;
  }

  if (!buffer) {
    return <div className="p-6 text-sm text-gray-600">Caricamento editor...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 mx-4">
        <div>
          <h1 className="text-sm font-semibold text-gray-900">
            Modifica live template {sesso === "F" ? "Donna" : "Uomo"}
            {letter?.nome ? ` — ${letter.nome}` : ""}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/lettere/${letterId}`}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Torna alla lettera
          </Link>

          <button
            type="button"
            onClick={handleSaveTemplate}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            {saving ? "Salvataggio..." : "Salva template"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="docx-editor-wrapper">

        <style>
          {`
            .docx-editor-wrapper .docx-outline-nav {
              display: none !important;
            }
          `}
        </style>

        <DocxEditor
          ref={editorRef}
          documentBuffer={buffer}
        />
      </div>
    </div>
  );
}